<template>
  <Teleport to="body">
    <Transition name="a11y-dialog">
      <div
        v-if="open"
        class="a11y-dialog-overlay"
        :class="overlayClass"
        @click.self="onOverlayClick"
      >
        <div
          ref="dialogRef"
          role="dialog"
          aria-modal="true"
          :aria-labelledby="titleId"
          :aria-describedby="describedBy || undefined"
          class="a11y-dialog"
          :class="contentClass"
          tabindex="-1"
          @keydown.esc.prevent="onEscape"
        >
          <slot
            :title-id="titleId"
            :close="requestClose"
          />
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed, toRef } from 'vue'
import { useFocusTrap } from '@/composables/useFocusTrap'

const props = withDefaults(
  defineProps<{
    open: boolean
    titleId?: string
    describedBy?: string
    closeOnOverlay?: boolean
    closeOnEscape?: boolean
    overlayClass?: string
    contentClass?: string
  }>(),
  {
    titleId: undefined,
    describedBy: undefined,
    closeOnOverlay: true,
    closeOnEscape: true,
    overlayClass: '',
    contentClass: '',
  }
)

const emit = defineEmits<{
  close: []
}>()

const dialogRef = ref<HTMLElement | null>(null)
const openRef = toRef(props, 'open')
useFocusTrap(dialogRef, openRef)

const generatedId = `a11y-dialog-title-${Math.random().toString(36).slice(2, 9)}`
const titleId = computed(() => props.titleId || generatedId)

function requestClose() {
  emit('close')
}

function onOverlayClick() {
  if (props.closeOnOverlay) requestClose()
}

function onEscape() {
  if (props.closeOnEscape) requestClose()
}

defineExpose({ titleId, dialogRef })
</script>

<style scoped>
.a11y-dialog-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 1rem;
}

.a11y-dialog {
  background: var(--background-secondary, #1a1d21);
  color: var(--text-primary, #fff);
  border-radius: var(--radius-lg, 16px);
  /* Ширина определяется контентом, чтобы вложенные карточки с собственным
     max-width не оставляли пустых полос внутри обёртки */
  width: fit-content;
  min-width: min(320px, 100%);
  max-width: min(600px, 100%);
  max-height: calc(100vh - 2rem);
  overflow-y: auto;
  outline: none;
  box-shadow: var(--shadow-xl, 0 12px 24px rgba(0, 0, 0, 0.25));
  border: 1px solid var(--border-primary, #2a2d31);
}

.a11y-dialog.invoice-dialog-shell,
.a11y-dialog.tx-detail-dialog {
  background: transparent;
  border: none;
  box-shadow: none;
  max-width: min(640px, 100%);
  overflow: visible;
}

.a11y-dialog.public-offer-dialog {
  width: min(760px, 100%);
  max-width: min(760px, 100%);
  overflow: hidden;
}

.a11y-dialog-enter-active,
.a11y-dialog-leave-active {
  transition: opacity 0.2s ease;
}

.a11y-dialog-enter-from,
.a11y-dialog-leave-to {
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .a11y-dialog-enter-active,
  .a11y-dialog-leave-active {
    transition: none;
  }
}
</style>
